import { Axe, Compass, GraduationCap } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import { sectionImages } from '../data/imageBank'

export default function Forest() {
  const { t } = useLanguage()
  const stats = [
    { value: '80 hectares', label: t('forest.stat1Label'), color: 'text-ng-mid' },
    { value: '200 m³', label: t('forest.stat2Label'), color: 'text-ng-brown' },
    { value: '100%', label: t('forest.stat3Label'), color: 'text-ng-gold' },
  ]
  const activities = [
    {
      icon: <Axe className="w-6 h-6 text-ng-brown" />,
      title: t('forest.activity1Title'),
      text: t('forest.activity1Text'),
    },
    {
      icon: <Compass className="w-6 h-6 text-ng-brown" />,
      title: t('forest.activity2Title'),
      text: t('forest.activity2Text'),
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-ng-brown" />,
      title: t('forest.activity3Title'),
      text: t('forest.activity3Text'),
    },
  ]

  return (
    <section id="forest" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Content */}
          <div>
            <span className="text-sm font-medium text-ng-brown uppercase tracking-wider">{t('forest.kicker')}</span>
            <h2 className="font-display text-4xl lg:text-5xl text-ng-dark mt-3 mb-6 font-bold">
              {t('forest.title')}
            </h2>
            <p className="text-lg text-ng-dark/70 leading-relaxed mb-10">
              {t('forest.intro')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-10">
              {stats.map(({ value, label, color }) => (
                <div key={label} className="text-center p-4 bg-ng-beige rounded-xl">
                  <div className={`font-display text-2xl lg:text-3xl font-bold ${color} mb-1`}>{value}</div>
                  <div className="text-xs text-ng-dark/60">{label}</div>
                </div>
              ))}
            </div>

            {/* Activities */}
            <div className="space-y-4">
              {activities.map(({ icon, title, text }) => (
                <div key={title} className="flex items-start gap-4 p-4 bg-ng-beige rounded-xl">
                  <div className="w-12 h-12 rounded-lg bg-ng-brown/10 flex items-center justify-center flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-ng-dark mb-1 font-semibold">{title}</h3>
                    <p className="text-sm text-ng-dark/60">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <img
              src={sectionImages.forest}
              alt={t('forest.imageAlt')}
              className="rounded-2xl shadow-xl w-full h-[600px] object-cover"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.parentElement.querySelector('.img-fallback').style.display = 'flex'
              }}
            />
            <div
              className="img-fallback rounded-2xl shadow-xl w-full h-[600px] hidden items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#1a472a 0%,#2d5a3d 50%,#3d7a4a 100%)' }}
            >
              <span className="text-white/20 text-sm">forest.jpg</span>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-ng-brown rounded-xl p-6 shadow-lg">
              <div className="font-display text-3xl font-bold text-white">80 ha</div>
              <div className="text-sm text-white/80">{t('forest.badgeText')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
